import React, { useEffect, useMemo, useRef, useState } from 'react';
import OptionSymbol from '../components/OptionSymbol';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate, faBriefcase, faChartLine, faIndianRupeeSign,
  faLayerGroup, faBolt, faSquare
} from '@fortawesome/free-solid-svg-icons';
import { addNotification } from '../utils/notifications';

const API = process.env.REACT_APP_API_URL || '';
const BROKERAGE_PER_ORDER = 11.8;
const fmt = (v) => `₹ ${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v) || 0;
const clean = (v) => String(v ?? '').trim();

const optionCharges = (buyAmt, sellAmt, brokerage = 20) => {
  const buy = num(buyAmt), sell = num(sellAmt), turnover = buy + sell;
  const exchange = turnover * 0.0003503;
  return brokerage + sell * 0.001 + turnover * 0.000001 + exchange + buy * 0.00003 + (brokerage + exchange) * 0.18;
};
const futureCharges = (buyAmt, sellAmt, brokerage = 20) => {
  const buy = num(buyAmt), sell = num(sellAmt), turnover = buy + sell;
  const exchange = turnover * 0.0000173;
  return brokerage + sell * 0.0005 + turnover * 0.000001 + exchange + buy * 0.00002 + (brokerage + exchange) * 0.18;
};

const getMaxQty = (symbol) => {
  const s = clean(symbol).toUpperCase();
  if (s.includes('BANKNIFTY')) return 600;
  if (s.includes('NIFTY')) return 1755;
  if (s.includes('SENSEX')) return 1000;
  return 1800;
};
const splitByMaxQty = (qty, symbol) => {
  const out = [];
  let left = Math.abs(Number(qty) || 0);
  const max = getMaxQty(symbol);
  while (left > 0) { out.push(Math.min(left, max)); left -= max; }
  return out;
};
const positionToken = (p) => clean(p?.tok ?? p?.token ?? p?.pSymbol ?? p?.exchangeToken ?? p?.symbolToken);
const quoteToken = (q) => clean(q?.exchange_token ?? q?.exchangeToken ?? q?.token ?? q?.symbolToken ?? q?.instrument_token);
const flattenQuotes = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenQuotes);
  if (typeof value !== 'object') return [];
  if (quoteToken(value) || value.ltp != null || value.last_price != null || value.lastPrice != null || value.last_traded_price != null || value.lp != null || value.LTP != null) return [value];
  return Object.values(value).flatMap(flattenQuotes);
};
const positionLtp = (p) => {
  const v = p?.ltp ?? p?.LTP ?? p?.lastPrice ?? p?.lp ?? p?.marketPrice ?? p?.mktPrice;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function Position() {
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [margin, setMargin] = useState(null);
  const [quoteMap, setQuoteMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [squaringKey, setSquaringKey] = useState('');
  const [squareAllBusy, setSquareAllBusy] = useState(false);
  const [squareDialog, setSquareDialog] = useState(null);
  const [squareOrderType, setSquareOrderType] = useState('MARKET');
  const [squareLimitPrice, setSquareLimitPrice] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [error, setError] = useState('');
  const fetching = useRef(false);

  const auth = () => ({
    baseUrl: localStorage.getItem('kotak_baseurl'),
    headers: { Auth: localStorage.getItem('kotak_trading_token'), Sid: localStorage.getItem('kotak_sid2') }
  });

  const load = async (silent = false) => {
    if (fetching.current) return;
    fetching.current = true;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const { baseUrl, headers } = auth();
      if (!baseUrl || !headers.Auth || !headers.Sid) throw new Error('Trading session expired. Please login again.');
      const serverId = localStorage.getItem('kotak_server_id') || '';
      const [pRes, oRes, mRes] = await Promise.all([
        axios.get(`${API}/api/kotak-positions`, { params: { baseUrl, sId: serverId }, headers }),
        axios.get(`${API}/api/kotak-orders`, { params: { baseUrl, sId: serverId }, headers }),
        axios.post(`${API}/api/kotak-limits`, { baseUrl, serverId, jData: { seg: 'ALL', exch: 'ALL', prod: 'ALL' } }, { headers })
      ]);
      const list = Array.isArray(pRes.data?.data) ? pRes.data.data : [];
      setPositions(list);
      setOrders(Array.isArray(oRes.data?.data) ? oRes.data.data : []);
      setMargin(mRes.data || null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to fetch positions.');
    } finally {
      setLoading(false); setRefreshing(false); fetching.current = false;
    }
  };

  const fetchPositionQuotes = async (list) => {
    const usable = list.filter(p => positionToken(p) && p.exSeg);
    if (!usable.length) { setQuoteMap({}); return; }
    try {
      const symbols = usable.map(p => `${p.exSeg}|${positionToken(p)}`).join(',');
      const response = await axios.get(`${API}/api/kotak-quotes`, {
        params: { symbols, baseUrl: localStorage.getItem('kotak_baseurl') },
        headers: { Authorization: localStorage.getItem('kotak_access_token') }
      });
      const quotes = flattenQuotes(response.data);
      const next = {};
      quotes.forEach((q) => {
        const token = quoteToken(q) || clean(q?.symbol);
        if (token) next[token] = q;
        const quoteSymbol = clean(q?.display_symbol ?? q?.trading_symbol ?? q?.pTrdSymbol ?? q?.symbol).toLowerCase();
        usable.forEach((position) => {
          const positionTokenValue = positionToken(position);
          const symbolsToMatch = [position?.trdSym, position?.sym, position?.pSymbol].map(clean).filter(Boolean).map(v => v.toLowerCase());
          if (positionTokenValue && token === positionTokenValue) next[positionTokenValue] = q;
          else if (positionTokenValue && quoteSymbol && symbolsToMatch.includes(quoteSymbol)) next[positionTokenValue] = q;
        });
      });
      setQuoteMap(next);
    } catch (err) {
      // Position page remains usable even when the quote service is temporarily unavailable.
      console.warn('Position market quote error:', err.message);
    }
  };

  useEffect(() => { load(false); }, []);
  useEffect(() => {
    if (positions.length) fetchPositionQuotes(positions);
    const timer = setInterval(() => { load(true); }, 3000);
    return () => clearInterval(timer);
  }, []); // position book refresh stays at 3s; market prices are refreshed below
  useEffect(() => {
    if (!positions.length) return undefined;
    fetchPositionQuotes(positions);
    const timer = setInterval(() => fetchPositionQuotes(positions), 1000);
    return () => clearInterval(timer);
  }, [positions]);

  const rows = useMemo(() => positions.map((p) => {
    const buy = num(p.buyAmt), sell = num(p.sellAmt);
    const netQty = num(p.flBuyQty) - num(p.flSellQty);
    const token = positionToken(p);
    const quote = quoteMap[token] || {};
    const quotePrice = Number(quote?.ltp ?? quote?.last_price ?? quote?.lastPrice ?? quote?.last_traded_price ?? quote?.lp ?? quote?.LTP);
    const fallbackPrice = positionLtp(p);
    const live = Number.isFinite(quotePrice) && quotePrice > 0 ? quotePrice : fallbackPrice;
    // Cash-flow P&L (sell turnover - buy turnover) plus the current market value
    // of the remaining signed net quantity. This includes realized and unrealized P&L.
    const pnl = sell - buy + (live !== null && live !== undefined && Number.isFinite(Number(live)) ? netQty * Number(live) : 0);
    return { ...p, buy, sell, pnl, netQty, token, quote, live };
  }).sort((a, b) => b.pnl - a.pnl), [positions, quoteMap]);

  const openRows = useMemo(() => rows.filter(p => p.netQty !== 0), [rows]);
  // In the broker position book, keep currently active positions at the top,
  // followed by closed/zero-quantity rows. Preserve P&L ordering within each group.
  const brokerRows = useMemo(() => [...rows].sort((a, b) => {
    const aActive = a.netQty !== 0 ? 1 : 0;
    const bActive = b.netQty !== 0 ? 1 : 0;
    return bActive - aActive || num(b.pnl) - num(a.pnl);
  }), [rows]);
  const completed = useMemo(() => orders.filter((o) => String(o.ordSt || '').toLowerCase() === 'complete'), [orders]);
  const mobileOrders = useMemo(() => orders.filter((o) => String(o.ordSrc || '').toUpperCase().includes('MOB')).length, [orders]);
  const apiOrders = useMemo(() => orders.filter((o) => !String(o.ordSrc || '').toUpperCase().includes('MOB')).length, [orders]);
  const totals = useMemo(() => {
    let buy = 0, sell = 0, tax = 0;
    positions.forEach((p) => {
      const b = num(p.buyAmt), s = num(p.sellAmt);
      buy += b; sell += s;
      tax += String(p.trdSym || '').toUpperCase().includes('FUT') ? futureCharges(b, s) : optionCharges(b, s);
    });
    const gross = rows.reduce((sum, p) => sum + num(p.pnl), 0);
    const brokerage = completed.filter((o) => o.ordSrc === 'ADMINCPPAPI_MOB').length * BROKERAGE_PER_ORDER;
    return { buy, sell, gross, tax, brokerage, net: gross - tax - brokerage };
  }, [positions, rows, completed]);

  const latestNetPnl = useRef(0);
  useEffect(() => { latestNetPnl.current = totals.net; }, [totals.net]);
  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;
    const sync = async () => {
      const netPnl = Number(latestNetPnl.current);
      if (!Number.isFinite(netPnl)) return;
      try {
        await axios.post(`${API}/api/pnl/current`, { accountKey: localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default', netPnl });
      } catch (error) {
        if (!cancelled) console.warn('PNL sync:', error.message);
      }
    };
    sync();
    const timer = setInterval(sync, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [loading]);
  const marginMetrics = useMemo(() => {
    const used = num(margin?.MarginUsed);
    const available = num(margin?.Net);
    return { used, available, remaining: available - used };
  }, [margin]);

  const openSquareOff = (p) => {
    if (!p || !p.netQty || squaringKey || squareAllBusy) return;
    setSquareDialog(p);
    setSquareOrderType('MARKET');
    setSquareLimitPrice(p.live != null && Number.isFinite(Number(p.live)) ? String(p.live) : '');
    setMessage({ type: '', text: '' });
  };

  const squareOff = async () => {
    const p = squareDialog;
    if (!p || !p.netQty || squaringKey || squareAllBusy) return;
    const side = p.netQty > 0 ? 'SELL' : 'BUY';
    const qty = Math.abs(p.netQty);
    const symbol = p.trdSym || p.sym || 'position';
    const isLimit = squareOrderType === 'LIMIT';
    const price = Number(squareLimitPrice);
    if (isLimit && (!Number.isFinite(price) || price <= 0)) {
      setMessage({ type: 'danger', text: 'Enter a valid limit price greater than zero.' });
      return;
    }
    const orderLabel = isLimit ? `Limit at ₹${price.toFixed(2)}` : 'Market';
    if (!window.confirm(`Square off ${qty} ${symbol} using ${orderLabel}?\nAction: ${side}`)) return;
    const key = `${p.exSeg}-${p.trdSym || p.sym || p.token}`;
    setSquaringKey(key);
    setMessage({ type: '', text: '' });
    try {
      const { baseUrl, headers } = auth();
      const serverId = localStorage.getItem('kotak_server_id') || '';
      const product = clean(p.prd ?? p.prod ?? p.product ?? p.productType) || 'NRML';
      const chunks = splitByMaxQty(qty, symbol);
      for (const chunk of chunks) {
        await axios.post(`${API}/api/place-order`, {
          orderData: {
            am: 'NO', dq: '0', es: p.exSeg, mp: '0', pc: product, pf: 'N',
            pr: isLimit ? price.toFixed(2) : '0', pt: isLimit ? 'L' : 'MKT',
            qt: String(chunk), rt: 'DAY', tp: '0',
            ts: p.trdSym || p.sym, tt: side === 'BUY' ? 'B' : 'S'
          }, baseUrl, auth: headers.Auth, sid: headers.Sid, serverId
        });
      }
      addNotification('Position squared off', `${symbol} · ${qty} qty · ${orderLabel} · ${side}`, 'success');
      setMessage({ type: 'success', text: `${symbol} square-off ${isLimit ? 'limit' : 'market'} order sent for ${qty} quantity.` });
      setSquareDialog(null);
      setTimeout(() => load(true), 400);
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || err.response?.data?.errMsg || 'Square-off order failed.' });
    } finally { setSquaringKey(''); }
  };

  const squareOffAll = async () => {
    if (!openRows.length || squareAllBusy || squaringKey) return;
    if (!window.confirm(`Square off ALL ${openRows.length} open positions at Market?`)) return;
    setSquareAllBusy(true); setMessage({ type: '', text: '' });
    let success = 0;
    try {
      for (const p of openRows) {
        try {
          const { baseUrl, headers } = auth();
          const serverId = localStorage.getItem('kotak_server_id') || '';
          const side = p.netQty > 0 ? 'SELL' : 'BUY';
          const product = clean(p.prd ?? p.prod ?? p.product ?? p.productType) || 'NRML';
          for (const chunk of splitByMaxQty(Math.abs(p.netQty), p.trdSym || p.sym)) {
            await axios.post(`${API}/api/place-order`, {
              orderData: { am:'NO', dq:'0', es:p.exSeg, mp:'0', pc:product, pf:'N', pr:'0', pt:'MKT', qt:String(chunk), rt:'DAY', tp:'0', ts:p.trdSym || p.sym, tt:side === 'BUY' ? 'B' : 'S' },
              baseUrl, auth:headers.Auth, sid:headers.Sid, serverId
            });
          }
          success += 1;
        } catch (err) {
          console.error('Square all failed:', p.trdSym || p.sym, err.response?.data || err.message);
        }
      }
      addNotification('Square off all', `${success}/${openRows.length} positions processed at Market`, success === openRows.length ? 'success' : 'warning');
      setMessage({ type: success === openRows.length ? 'success' : 'warning', text: `${success} of ${openRows.length} open positions square-off orders sent.` });
      setTimeout(() => load(true), 500);
    } finally { setSquareAllBusy(false); }
  };

  const marketPrice = (p) => p.live !== null && p.live !== undefined ? fmt(p.live) : '—';

  return (
    <main className="terminal-page position-page">
      {error && <div className="alert alert-danger mb-3">{error}</div>}
      {message.text && <div className={`alert alert-${message.type || 'info'} mb-3 d-flex justify-content-between align-items-center`}><span>{message.text}</span><button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })} /></div>}

      {/* 1. Active open positions */}
      <section className="card position-open-card mb-4">
        <div className="position-card-head position-open-head">
          <div><small>ACTIVE POSITIONS</small><h5>Open positions</h5><span>Market price and one-tap square-off</span></div>
          <div className="position-head-actions"><span className="position-open-count">{openRows.length} Open</span><button className="btn btn-sm btn-danger" onClick={squareOffAll} disabled={!openRows.length || squareAllBusy || !!squaringKey}><FontAwesomeIcon icon={squareAllBusy ? faArrowsRotate : faSquare} className={squareAllBusy ? 'me-1 fa-spin' : 'me-1'} />{squareAllBusy ? 'Squaring…' : 'Square Off All · Market'}</button></div>
        </div>
        {loading ? <div className="position-empty"><div className="spinner-border text-success mb-3"/><div>Loading active positions…</div></div> : !openRows.length ? <div className="position-empty"><FontAwesomeIcon icon={faBriefcase}/><b>No open positions</b><span>Active positions will appear here.</span></div> : <div className="active-position-grid">
          {openRows.map((p, i) => {
            const key = `${p.exSeg}-${p.trdSym || p.sym || p.token}`;
            const side = p.netQty > 0 ? 'SELL' : 'BUY';
            const busy = squaringKey === key;
            return <article className="active-position-tile" key={key}>
              <div className="active-position-top"><div><b><OptionSymbol symbol={p.trdSym || p.sym || '—'} strike={p.stkPrc} optionType={p.optTp} /></b><small>{p.exSeg || '—'}</small></div><span className={p.netQty > 0 ? 'position-side long' : 'position-side short'}>{p.netQty > 0 ? 'LONG' : 'SHORT'}</span></div>
              <div className="active-position-values"><div><span>Net Qty</span><b className={p.netQty > 0 ? 'text-success' : 'text-danger'}>{p.netQty}</b></div><div><span>Avg Price</span><b>{p.netQty > 0 && num(p.flBuyQty) ? fmt(p.buy / num(p.flBuyQty)) : p.netQty < 0 && num(p.flSellQty) ? fmt(p.sell / num(p.flSellQty)) : '—'}</b></div><div><span>Market Price</span><b>{marketPrice(p)}</b></div><div><span>Position P&amp;L</span><b className={p.pnl >= 0 ? 'text-success' : 'text-danger'}>{p.pnl >= 0 ? '+' : ''}{fmt(p.pnl)}</b></div></div>
              <button className={`btn active-square-btn ${side === 'SELL' ? 'sell' : 'buy'}`} disabled={busy || squareAllBusy} onClick={() => openSquareOff(p)}><FontAwesomeIcon icon={busy ? faArrowsRotate : faBolt} className={busy ? 'me-2 fa-spin' : 'me-2'} />Square Off {side}</button>
            </article>;
          })}
        </div>}
      </section>

      {/* 2. Broker position book */}
      <section className="card position-table-card mb-4">
        <div className="position-card-head"><div><small>BROKER POSITION BOOK</small><h5>Current broker positions</h5></div><button className="btn btn-sm btn-outline-success" onClick={() => load(true)} disabled={refreshing}><FontAwesomeIcon icon={faArrowsRotate} className={refreshing ? 'me-1 fa-spin' : 'me-1'} />{refreshing ? 'Refreshing…' : 'Refresh'}</button></div>
        {loading ? <div className="position-empty"><div className="spinner-border text-success mb-3"/><div>Loading positions…</div></div> : rows.length === 0 ? <div className="position-empty"><FontAwesomeIcon icon={faBriefcase}/><b>No positions available</b><span>Your Kotak position book is currently empty.</span></div> : <>
          <div className="table-responsive position-desktop-book d-none d-md-block"><table className="table position-table align-middle mb-0"><thead><tr><th>Instrument</th><th className="text-center">Net Qty</th><th>Buy Amount</th><th>Sell Amount</th><th className="text-end">Market Price</th><th className="text-end">P&amp;L</th></tr></thead><tbody>{brokerRows.map((p,i) => <tr key={`${p.tok || p.sym || i}-${i}`}><td><b><OptionSymbol symbol={p.trdSym || p.sym || '—'} strike={p.stkPrc} optionType={p.optTp} /></b><small>{p.exSeg || '—'}</small></td><td className="text-center"><span className={p.netQty > 0 ? 'qty-badge buy' : p.netQty < 0 ? 'qty-badge sell' : 'qty-badge'}>{p.netQty}</span></td><td>{fmt(p.buy)}</td><td>{fmt(p.sell)}</td><td className="text-end">{marketPrice(p)}</td><td className={`text-end fw-bold ${p.pnl >= 0 ? 'text-success' : 'text-danger'}`}>{p.pnl >= 0 ? '+' : ''}{fmt(p.pnl)}</td></tr>)}</tbody></table></div>
          <div className="position-mobile-book d-md-none">{brokerRows.map((p,i) => <article className="position-mobile-row" key={`mobile-${p.tok || p.sym || i}-${i}`}>
            <div className="position-mobile-instrument"><b><OptionSymbol symbol={p.trdSym || p.sym || '—'} strike={p.stkPrc} optionType={p.optTp} /></b><small>{p.exSeg || '—'}</small></div>
            <div className="position-mobile-quantity"><span className={p.netQty > 0 ? 'qty-badge buy' : p.netQty < 0 ? 'qty-badge sell' : 'qty-badge'}>{p.netQty}</span></div>
            <div className="position-mobile-market"><b className={p.pnl >= 0 ? 'positive' : 'negative'}>{p.pnl >= 0 ? '+' : ''}{fmt(p.pnl)}</b><small>LTP : {marketPrice(p)}</small></div>
          </article>)}</div>
        </>}
      </section>

      {/* 3. Trading P&L summary — separate from the Daily PNL module */}
      <section className="card pnl-summary-card mb-4">
        <div className="position-card-head"><div><small>TRADING P&amp;L</small><h5>Trading P&amp;L summary</h5><span>Live position-book calculation for this trading session</span></div><FontAwesomeIcon icon={faChartLine}/></div>
        <div className="pnl-summary-grid">
          <div><span>BUY TURNOVER</span><b>{fmt(totals.buy)}</b></div>
          <div><span>SELL TURNOVER</span><b>{fmt(totals.sell)}</b></div>
          <div><span>GROSS P&amp;L</span><b className={totals.gross >= 0 ? 'text-success' : 'text-danger'}>{totals.gross >= 0 ? '+' : ''}{fmt(totals.gross)}</b></div>
          <div><span>CHARGES</span><b>{fmt(totals.tax)}</b></div>
          <div><span>BROKERAGE</span><b>{fmt(totals.brokerage)}</b></div>
          <div className="net-summary"><span>NET P&amp;L</span><b className={totals.net >= 0 ? 'text-success' : 'text-danger'}>{totals.net >= 0 ? '+' : ''}{fmt(totals.net)}</b></div>
        </div>
      </section>

      {/* 4. Available margin */}
      <section className="card margin-card mb-4">
        <div className="position-card-head"><div><small>AVAILABLE MARGIN</small><h5>Margin overview</h5><span>Live account funds from Kotak limits API</span></div><FontAwesomeIcon icon={faIndianRupeeSign}/></div>
        {loading && !margin ? <div className="p-4 text-muted">Loading margin…</div> : <><div className="margin-list"><div><span>Available / Net</span><b>{fmt(marginMetrics.available)}</b></div><div><span>Used Margin</span><b>{fmt(marginMetrics.used)}</b></div><div className={marginMetrics.remaining >= 0 ? 'remaining positive' : 'remaining negative'}><span>Remaining Margin</span><b>{fmt(marginMetrics.remaining)}</b></div></div><div className="margin-progress"><span style={{ width: `${marginMetrics.available > 0 ? Math.min(100, (marginMetrics.used / marginMetrics.available) * 100) : 0}%` }}/></div><div className="account-stats"><div><FontAwesomeIcon icon={faChartLine}/><span>Mobile Orders</span><b>{mobileOrders}</b></div><div><FontAwesomeIcon icon={faLayerGroup}/><span>API Orders</span><b>{apiOrders}</b></div><div><FontAwesomeIcon icon={faLayerGroup}/><span>Total Orders</span><b>{orders.length}</b></div></div></>}
      </section>

      {squareDialog && <div className="position-squareoff-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !squaringKey) setSquareDialog(null); }}>
        <section className="position-squareoff-dialog" role="dialog" aria-modal="true" aria-labelledby="squareoff-dialog-title">
          <div className="position-squareoff-dialog-head">
            <div><small>POSITION SQUARE-OFF</small><h5 id="squareoff-dialog-title"><OptionSymbol symbol={squareDialog.trdSym || squareDialog.sym || 'Position'} strike={squareDialog.stkPrc} optionType={squareDialog.optTp} /></h5></div>
            <button type="button" className="position-squareoff-close" aria-label="Close" onClick={() => !squaringKey && setSquareDialog(null)}>×</button>
          </div>
          <div className="position-squareoff-meta">
            <span>Action <b className={squareDialog.netQty > 0 ? 'text-danger' : 'text-success'}>{squareDialog.netQty > 0 ? 'SELL' : 'BUY'}</b></span>
            <span>Quantity <b>{Math.abs(squareDialog.netQty)}</b></span>
            <span>Market Price <b>{marketPrice(squareDialog)}</b></span>
          </div>
          <div className="position-squareoff-body">
            <label className="form-label fw-semibold">Order Type</label>
            <div className="position-squareoff-types">
              <button type="button" className={squareOrderType === 'MARKET' ? 'selected' : ''} onClick={() => setSquareOrderType('MARKET')}>Market</button>
              <button type="button" className={squareOrderType === 'LIMIT' ? 'selected' : ''} onClick={() => setSquareOrderType('LIMIT')}>Limit Price</button>
            </div>
            {squareOrderType === 'LIMIT' && <div className="mt-3"><label className="form-label fw-semibold" htmlFor="squareoff-limit-price">Limit Price (₹)</label><input id="squareoff-limit-price" className="form-control" type="number" min="0.05" step="0.05" inputMode="decimal" value={squareLimitPrice} onChange={(e) => setSquareLimitPrice(e.target.value)} placeholder="Enter limit price" autoFocus /></div>}
            <div className="position-squareoff-note">{squareOrderType === 'MARKET' ? 'A market order executes at the available market price.' : 'A limit order is submitted at your entered price and may not execute immediately.'}</div>
          </div>
          <div className="position-squareoff-actions"><button type="button" className="btn btn-light" onClick={() => setSquareDialog(null)} disabled={!!squaringKey}>Cancel</button><button type="button" className={`btn ${squareDialog.netQty > 0 ? 'btn-danger' : 'btn-success'}`} onClick={squareOff} disabled={!!squaringKey}>{squaringKey ? 'Submitting…' : `Confirm ${squareDialog.netQty > 0 ? 'Sell' : 'Buy'} Square-Off`}</button></div>
        </section>
      </div>}
    </main>
  );
}
